// ============================================================================
// KLINISOL CRO - About Page (REDESIGNED)
// Matches Home page design system
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// ============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { 
    Target, 
    Handshake, 
    Lightbulb, 
    Users,
    Shield,
    Activity,
    Award,
    Building2,
    ArrowRight
} from 'lucide-react';

const About = () => {
    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Hero Section */}
            <section className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-16 px-6">
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">
                        About KLINiSOL
                    </h1>
                    <p className="text-xl max-w-3xl mx-auto">
                        Advancing Investigator-Initiated Research
                    </p>
                </div>
            </section>

            {/* Who We Are */}
            <section className="py-16 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <h2 className="text-3xl font-bold text-[#475569] mb-6">Who We Are</h2>
                        <p className="text-lg text-gray-700 leading-relaxed">
                            KLIN<span className="text-[#E91E63]">i</span>SOL is a boutique Contract Research Organization (CRO) specializing in 
                            investigator-initiated clinical trials. We provide comprehensive support to researchers, 
                            sponsors, and clinical sites throughout the entire clinical trial lifecycle.
                        </p>
                    </div>

                    {/* Mission & Vision */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
                        <div className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-xl p-8 border-2 border-[#475569]/20 hover:border-[#E91E63] hover:shadow-lg transition-all">
                            <Target className="w-12 h-12 text-[#E91E63] mb-4" />
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                            <p className="text-gray-700 leading-relaxed">
                                To advance medical research by providing exceptional clinical trial management 
                                services that ensure scientific rigor, regulatory compliance, and patient safety.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-[#E91E63]/5 to-[#475569]/5 rounded-xl p-8 border-2 border-[#E91E63]/20 hover:border-[#E91E63] hover:shadow-lg transition-all">
                            <Lightbulb className="w-12 h-12 text-[#E91E63] mb-4" />
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
                            <p className="text-gray-700 leading-relaxed">
                                To be the leading CRO partner for investigator-initiated research, recognized for 
                                our expertise, integrity, and commitment to advancing healthcare innovation.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Values */}
            <section className="py-16 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <h2 className="text-3xl font-bold text-[#475569] text-center mb-12">Our Core Values</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            {
                                icon: Target,
                                title: "Excellence",
                                desc: "We maintain the highest standards in everything we do"
                            },
                            {
                                icon: Handshake,
                                title: "Integrity",
                                desc: "We operate with transparency and ethical principles"
                            },
                            {
                                icon: Lightbulb,
                                title: "Innovation",
                                desc: "We embrace new technologies and methodologies"
                            },
                            {
                                icon: Users,
                                title: "Collaboration",
                                desc: "We work as partners with our clients and stakeholders"
                            }
                        ].map((value, index) => (
                            <div 
                                key={index}
                                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-[#E91E63] transition-all text-center"
                            >
                                <value.icon className="w-12 h-12 text-[#E91E63] mx-auto mb-4" />
                                <h4 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h4>
                                <p className="text-gray-600">{value.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Expertise */}
            <section className="py-16 px-6">
                <div className="max-w-7xl mx-auto">
                    <h2 className="text-3xl font-bold text-[#475569] text-center mb-12">Our Expertise</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[
                            {
                                icon: Shield,
                                title: "Regulatory Compliance",
                                desc: "FDA 21 CFR Part 11 compliant systems and ICH-GCP E6(R3) certified processes"
                            },
                            {
                                icon: Activity,
                                title: "Therapeutic Areas",
                                desc: "Specialized in Dermatology, Cardiovascular, and Gastroenterology trials"
                            },
                            {
                                icon: Award,
                                title: "Quality Management",
                                desc: "Comprehensive quality assurance and audit-ready documentation systems"
                            },
                            {
                                icon: Building2,
                                title: "Site Management",
                                desc: "Expert site selection, training, and monitoring services"
                            }
                        ].map((item, index) => (
                            <div 
                                key={index}
                                className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 hover:shadow-lg hover:border-[#E91E63] transition-all"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-14 h-14 bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10 rounded-xl flex items-center justify-center">
                                            <item.icon className="w-8 h-8 text-[#E91E63]" />
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h4>
                                        <p className="text-gray-600">{item.desc}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 px-6 bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">
                        Partner With Us
                    </h2>
                    <p className="text-lg text-gray-700 mb-8">
                        Let's discuss how KLIN<span className="text-[#E91E63]">i</span>SOL can support your clinical research goals
                    </p>
                    <Link 
                        to="/contact" 
                        className="inline-flex items-center gap-2 px-8 py-4 bg-white border-2 border-[#475569] text-[#475569] font-bold rounded-lg hover:border-[#E91E63] hover:shadow-xl transition-all text-lg group"
                    >
                        <span>Contact Us</span>
                        <ArrowRight className="w-6 h-6 group-hover:text-[#E91E63] transition-colors" />
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-12 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                        <div>
                            <h3 className="text-2xl font-bold mb-2">
                                KLIN<span className="text-[#E91E63]">i</span>SOL
                            </h3>
                            <p className="text-sm opacity-90">Advancing Investigator-Initiated Research</p>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold mb-3">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/" className="hover:text-[#E91E63] transition-colors">Home</Link></li>
                                <li><Link to="/services" className="hover:text-[#E91E63] transition-colors">Services</Link></li>
                                <li><Link to="/therapeutic-areas" className="hover:text-[#E91E63] transition-colors">Therapeutic Areas</Link></li>
                                <li><Link to="/contact" className="hover:text-[#E91E63] transition-colors">Contact</Link></li>
                            </ul>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold mb-3">Contact</h4>
                            <p className="text-sm opacity-90 mb-1">600 Broadway Ste 200 #2189</p>
                            <p className="text-sm opacity-90 mb-2">Albany, NY 12207</p>
                            <p className="text-sm opacity-90 mb-1">Email: info@klinisol.com</p>
                            <p className="text-sm opacity-90">Website: klinisol.com</p>
                        </div>
                    </div>
                    
                    <div className="border-t border-white/20 pt-6 text-center">
                        <p className="text-sm opacity-90 mb-4">&copy; 2025 KLINISOL. All rights reserved.</p>
                        
                        {/* Social Media Links */}
                        <div className="flex items-center justify-center gap-4">
                            <a 
                                href="https://www.linkedin.com/company/klinisol/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                                aria-label="LinkedIn"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                </svg>
                            </a>
                            
                            <a 
                                href="https://youtube.com/@klinisol721" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                                aria-label="YouTube"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default About;